import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, TrendingUp, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("grc_admin_token")}` });

const CHANNEL_LABEL = {
  instagram: "IG", facebook: "FB", linkedin: "LI", email: "Email",
  text: "Text", print: "Print", in_person: "In person", other: "Other",
};

export const AssetLeaderboardPanel = () => {
  const [months, setMonths] = useState(3);
  const [data, setData] = useState(null);

  const load = useCallback(async (m) => {
    try {
      const { data: d } = await axios.get(`${API}/admin/bdm-asset-leaderboard?months=${m}`, { headers: authHeader() });
      setData(d);
    } catch {
      setData({ rows: [], folders: [], untouched: [] });
    }
  }, []);

  useEffect(() => { load(months); }, [load, months]);

  if (!data) {
    return (
      <div className="py-16 flex justify-center rounded-2xl bg-white border border-linen-border">
        <Loader2 className="animate-spin text-teal" size={24} />
      </div>
    );
  }

  const rows = data.rows || [];
  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="admin-asset-leaderboard">
      <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
              <TrendingUp size={13} /> Asset leaderboard
            </div>
            <h3 className="mt-1 font-heading font-medium text-xl text-navy">What partners actually use</h3>
            <p className="mt-1 text-sm text-ink-500">
              <strong>Grabs</strong> are downloads — intent. <strong>Uses</strong> are what a partner confirmed
              they put out. Uses is the number that should drive what you make next.
            </p>
          </div>
          <select
            value={months}
            onChange={(e) => setMonths(Number(e.target.value))}
            data-testid="admin-leaderboard-range"
            className="rounded-full border border-linen-border bg-white px-3 py-1.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-teal/40 shrink-0"
          >
            <option value={1}>This month</option>
            <option value={3}>Last 3 months</option>
            <option value={6}>Last 6 months</option>
            <option value={12}>Last 12 months</option>
          </select>
        </div>
        <dl className="mt-4 grid grid-cols-3 gap-3 max-w-md">
          {[
            ["Uses", totals.uses || 0],
            ["Grabs", totals.grabs || 0],
            ["Assets touched", totals.assets_touched || 0],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl bg-linen/70 border border-linen-border px-3 py-2">
              <dt className="text-[10px] uppercase tracking-widest text-ink-500">{label}</dt>
              <dd className="text-lg font-heading text-navy tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-6">
          <p className="text-sm text-ink-500">
            Nothing to rank yet. Numbers appear once partners start downloading and logging from their shelves.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-linen-border shadow-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-leaderboard-table">
            <thead>
              <tr className="text-left text-ink-500 border-b border-linen-border bg-linen/50">
                <th className="p-3 font-medium">#</th>
                <th className="p-3 font-medium">Asset</th>
                <th className="p-3 font-medium">Folder</th>
                <th className="p-3 font-medium text-center">Uses</th>
                <th className="p-3 font-medium text-center">Grabs</th>
                <th className="p-3 font-medium text-center">Partners</th>
                <th className="p-3 font-medium">Where it went</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.asset_id} className="border-b border-linen-border/60" data-testid={`admin-leaderboard-row-${r.asset_id}`}>
                  <td className="p-3 text-ink-400 tabular-nums">{i + 1}</td>
                  <td className="p-3">
                    <span className="font-medium text-navy">{r.filename || "Untitled"}</span>
                    {r.deleted && <span className="ml-2 text-[10px] uppercase tracking-widest text-ink-400">deleted</span>}
                    {r.last_used_on ? <div className="text-[11px] text-ink-400">last used {r.last_used_on}</div> : null}
                  </td>
                  <td className="p-3 text-ink-500 text-xs">{r.folder_name || "—"}</td>
                  <td className="p-3 text-center font-semibold text-navy tabular-nums">{r.uses}</td>
                  <td className="p-3 text-center text-ink-500 tabular-nums">{r.grabs}</td>
                  <td className="p-3 text-center text-ink-500 tabular-nums">{r.partners}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(r.channels || {}).map(([ch, n]) => (
                        <span key={ch} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-linen text-ink-500">
                          {CHANNEL_LABEL[ch] || ch} {n}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.untouched_total > 0 && (
        <div className="rounded-2xl bg-white border border-linen-border shadow-soft p-5" data-testid="admin-leaderboard-untouched">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-ink-500">
            <EyeOff size={13} /> Nobody has touched these
          </div>
          <h3 className="mt-1 font-heading font-medium text-lg text-navy">
            {data.untouched_total} shared asset{data.untouched_total === 1 ? "" : "s"} with no downloads at all
          </h3>
          <p className="mt-1 text-sm text-ink-500">
            Either they're not what partners need, or nobody knows they're there — try a shelf drop announcement
            before writing them off.
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-1">
            {data.untouched.map((u) => (
              <li key={u.asset_id} className="text-xs text-ink-500 truncate">
                {u.filename} <span className="text-ink-400">· {u.folder_name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AssetLeaderboardPanel;
